import { useTeamsAndChannels } from "@/context/TeamsAndChannelsContext";
import { TeamsFile } from "@/util/interfaces/TeamsFile";

export const useTeamsFiles = () => {
  const { teamsFiles, setTeamsFiles, selectedRows, setSelectedRows } = useTeamsAndChannels();

  const addTeamsFiles = (newFiles: TeamsFile[]) => {
    setTeamsFiles((prevFiles) => {
      // Merge files avoiding duplicates based on ID
      const existingIds = new Set(prevFiles.map((file) => file.id));
      const uniqueNewFiles = newFiles.filter((file) => !existingIds.has(file.id));
      return [...prevFiles, ...uniqueNewFiles];
    });
  };

  const updateTeamsFile = (fileId: string, updatedFile: Partial<TeamsFile>) => {
    setTeamsFiles((prevFiles) =>
      prevFiles.map((file) => (file.id === fileId ? { ...file, ...updatedFile } : file))
    );
  };

  const removeTeamsFile = (fileId: string) => {
    setTeamsFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
  };

  const clearTeamsFiles = () => {
    setTeamsFiles([]);
  };

  const getTeamsFileById = (fileId: string): TeamsFile | undefined => {
    return teamsFiles.find((file) => file.id === fileId);
  };

  const getTeamsFilesByParentId = (parentId: string): TeamsFile[] => {
    return teamsFiles.filter((file) => file.parentReference.id === parentId);
  };

  const getTeamsFilesByChannel = (driveId: string, itemId: string): TeamsFile[] => {
    return teamsFiles.filter(
      (file) => file.channel.drive_id === driveId && file.channel.item_id === itemId
    );
  };

  const addSelectedRow = (file: TeamsFile) => {
    setSelectedRows((prev: string[]) => {
      const exists = prev.includes(file.id);
      return exists ? prev : [...prev, file.id];
    });
  };

  const removeSelectedRow = (fileId: string) => {
    setSelectedRows((prev: string[]) => prev.filter((id: string) => id !== fileId));
  };

  const clearSelectedRows = () => {
    setSelectedRows([]);
  };

  const isRowSelected = (fileId: string): boolean => {
    return selectedRows.includes(fileId);
  };

  const getSelectedFiles = (): TeamsFile[] => {
    return teamsFiles.filter((file) => selectedRows.includes(file.id));
  };

  const getSelectedFilesByParentId = (parentId: string): TeamsFile[] => {
    return teamsFiles.filter(
      (file) => selectedRows.includes(file.id) && file.parentReference.id === parentId
    );
  };

  const getAllSelectedFiles = (): TeamsFile[] => {
    return teamsFiles.filter((file) => file.selected === true);
  };

  return {
    teamsFiles,
    setTeamsFiles,
    addTeamsFiles,
    updateTeamsFile,
    removeTeamsFile,
    clearTeamsFiles,
    getTeamsFileById,
    getTeamsFilesByParentId,
    getTeamsFilesByChannel,
    selectedRows,
    setSelectedRows,
    addSelectedRow,
    removeSelectedRow,
    clearSelectedRows,
    isRowSelected,
    getSelectedFiles,
    getSelectedFilesByParentId,
    getAllSelectedFiles,
  };
};
